import React, {ReactNode} from "react";
import {UsersAndDataPage} from "../../../administration/UsersAndDataPage";
import {AuthRedirectCallback, AuthSilentRedirectCallback} from "../../technical/auth/AuthCallbacks";
import {isVisible, WithRestrictedVisibility} from "../../technical/security/WithRestrictedVisibility";
import {PersonalDataPage} from "../../../account/PersonalDataPage";
import {NoAccountServiceAccessPage} from "../page/NoAccountServiceAccessPage";
import {NoApplicationAccessPage} from "../page/NoApplicationAccessPage";
import {ApplicationsPage} from "../../../administration/ApplicationsPage";
import {StandardRole} from "../../../administration/users/Role";
import {SolutionsPage} from "../../../account/SolutionsPage";
import {User} from "../../../administration/users/User";
import {ApplicationPage} from "../../../administration/applications/ApplicationPage";
import {AuditLogPage} from "../../../administration/AuditLogPage";

export interface Route {
  path: string,
  component: React.ReactNode,
  secured: boolean,
  drawerEntry?: {
    titleKey: string,
    drawerGroup?: string,
    icon?: ReactNode,
    dividerBefore?: boolean,
    dividerAfter?: boolean,
  } & WithRestrictedVisibility
}

export const HOME_ROUTE: Route = {path: '/', component: <NoAccountServiceAccessPage />, secured: true,};
export const AUTH_REDIRECT_ROUTE: Route = {path: '/auth/redirect', component: <AuthRedirectCallback />, secured: false};
export const AUTH_SILENT_REDIRECT_ROUTE: Route = {path: '/auth/silent_redirect', component: <AuthSilentRedirectCallback />, secured: false};

export const routePaths = {
  userAndData: '/administration/users-and-data',
  userAndDataWithSelection: '/administration/users-and-data/:selection',
  userAndDataWithSelectionAndId: '/administration/users-and-data/:selection/:id',
  auditLog: '/administration/audit-log',
  application: '/administration/applications',
  applicationWithId: '/administration/applications/:applicationId',
}
export const routes: Route[] = [
  HOME_ROUTE,
  {
    path: '/account/personal-data',
    component: <PersonalDataPage />,
    secured: true,
    drawerEntry: {
      titleKey: 'personalData',
      visibleForRoles: [StandardRole.ADMIN, StandardRole.APPLICATION_ADMIN, StandardRole.USER, StandardRole.COMPANY_ADMIN]
    }
  }, {
    path: '/account/solutions',
    component: <SolutionsPage />,
    secured: true,
    drawerEntry: {
      titleKey: 'solutions',
      dividerAfter: true,
      visibleForRoles: [StandardRole.ADMIN, StandardRole.APPLICATION_ADMIN, StandardRole.USER, StandardRole.COMPANY_ADMIN]
    }
  }, {
    path: routePaths.userAndData,
    component: <UsersAndDataPage />,
    secured: true,
    drawerEntry: {
      titleKey: 'usersAndData',
      drawerGroup: 'administration',
      visibleForRoles: [StandardRole.ADMIN, StandardRole.APPLICATION_ADMIN, StandardRole.COMPANY_ADMIN]
    }
  },
  {
    path: routePaths.userAndDataWithSelection,
    component: <UsersAndDataPage />,
    secured: true
  },
  {
    path: routePaths.userAndDataWithSelectionAndId,
    component: <UsersAndDataPage />,
    secured: true
  },
  {
    path: routePaths.application,
    component: <ApplicationsPage />,
    secured: true,
    drawerEntry: {
      titleKey: 'applications',
      drawerGroup: 'administration',
      dividerAfter: true,
      visibleForRoles: [StandardRole.ADMIN, StandardRole.APPLICATION_ADMIN]
    }
  }, {
    path: routePaths.auditLog,
    component: <AuditLogPage />,
    secured: true,
    drawerEntry: {
      titleKey: 'auditLog',
      drawerGroup: 'administration',
      visibleForRoles: [StandardRole.ADMIN]
    }
  }, {
    path: routePaths.applicationWithId,
    component: <ApplicationPage />,
    secured: true
  }, {
    path: '/no-access',
    component: <NoApplicationAccessPage />,
    secured: true,
  },
  AUTH_REDIRECT_ROUTE,
  AUTH_SILENT_REDIRECT_ROUTE,
];

export const drawerRoutes = routes.filter((route) => !!route.drawerEntry)
export const securedRoutes = routes.filter((route) => route.secured)
export const unsecuredRoutes = routes.filter((route) => !route.secured)

export function routeVisibleForUser(account: User) {
  return (route: Route) => !route.secured || (!route.drawerEntry || isVisible(account, route.drawerEntry));
}

export function routeAccessibleForUser(account: User) {
  return (route: Route) => !route.drawerEntry || isVisible(account, route.drawerEntry);
}

export function routeNotVisibleForUser(account: User) {
  return (route: Route) => route.secured && (route.drawerEntry && !isVisible(account, route.drawerEntry));
}

